import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';

interface StyledStatusContainerProps {
  statusType:
    | 'active'
    | 'inactive'
    | 'closed'
    | 'suspended'
    | 'new'
    | 'pending'
    | 'pending approval'
    | 'approved'
    | 'rejected'
    | 'payment progress'
    | 'payment pending'
    | 'paid'
    | 'completed'
    | 'payment completed'
    | 'issued'
    | 'realized'
    | 'returned'
    | 'cancelled'
    | 'verified'
    | 'unverified';
}

export const StyledStatusContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'statusType',
})<StyledStatusContainerProps>(({ statusType }) => {
  const getBackgroundColor = () => {
    switch (statusType) {
      case 'active':
      case 'approved':
      case 'verified':
        return 'var(--active-bg)';
      case 'paid':
      case 'completed':
        return 'var(--completed-bg)';
      case 'payment completed':
        return 'var(--payment-completed-bg, #F3E5F5)';
      case 'issued':
        return 'var(--issued-bg, #E3F2FD)';
      case 'realized':
        return 'var(--realized-bg, #E8F5E9)';
      case 'returned':
        return 'var(--returned-bg, #FFF3E0)';
      case 'cancelled':
        return 'var(--cancelled-bg, #FFEBEE)';
      case 'inactive':
      case 'rejected':
      case 'unverified':
        return 'var(--inactive-bg)';
      case 'closed':
      case 'suspended':
        return 'var(--closed-bg)';
      case 'pending':
      case 'pending approval':
        return 'var(--warning-light)';
      case 'payment pending':
        return 'var(--payment-pending-bg, #FFFDE7)';
      case 'new':
        return 'var(--new-light)';
      case 'payment progress':
        return 'var(--info-light, #E3F2FD)';
      default:
        return 'var(--bg-color)';
    }
  };

  const getWidth = () => {
    switch (statusType) {
      case 'suspended':
        return '85px';
      case 'approved':
        return '75px';
      case 'new':
        return '50px';
      case 'pending approval':
        return '115px';
      case 'payment progress':
        return '120px';
      case 'payment pending':
        return '125px';
      case 'payment completed':
        return '140px';
      case 'completed':
        return '86px';
      case 'paid':
        return '55px';
      case 'issued':
        return '65px';
      case 'realized':
        return '75px';
      case 'returned':
        return '75px';
      case 'cancelled':
        return '80px';
      case 'verified':
        return '75px';
      case 'unverified':
        return '88px';
      default:
        return '65px';
    }
  };

  return {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: getBackgroundColor(),
    borderRadius: '50px',
    padding: '4px 8px',
    textAlign: 'center',
    width: getWidth(),
  };
});

interface StyledStatusIconProps {
  statusType:
    | 'active'
    | 'inactive'
    | 'closed'
    | 'suspended'
    | 'new'
    | 'pending'
    | 'pending approval'
    | 'approved'
    | 'rejected'
    | 'payment progress'
    | 'payment pending'
    | 'paid'
    | 'completed'
    | 'payment completed'
    | 'issued'
    | 'realized'
    | 'returned'
    | 'cancelled'
    | 'verified'
    | 'unverified';
}

export const StyledStatusIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'statusType',
})<StyledStatusIconProps>(({ statusType }) => {
  const getColor = () => {
    switch (statusType) {
      case 'active':
      case 'approved':
      case 'verified':
        return 'var(--success)';
      case 'paid':
      case 'completed':
        return 'var(--completed)';
      case 'payment completed':
        return 'var(--payment-completed, #7B1FA2)';
      case 'inactive':
      case 'rejected':
      case 'unverified':
        return 'var(--error)';
      case 'pending':
      case 'pending approval':
        return 'var(--warning)';
      case 'payment pending':
        return 'var(--payment-pending, #EF6C00)';
      case 'new':
        return 'var(--new-status)';
      case 'closed':
      case 'suspended':
        return 'var(--instructions)';
      case 'payment progress':
        return 'var(--info, #1976D2)';
      case 'issued':
        return 'var(--issued, #1976D2)';
      case 'realized':
        return 'var(--realized, #388E3C)';
      case 'returned':
        return 'var(--returned, #F57C00)';
      case 'cancelled':
        return 'var(--cancelled, #D32F2F)';
      default:
        return 'var(--primary-navy)';
    }
  };

  const getFontSize = () => {
    switch (statusType) {
      case 'active':
      case 'new':
        return '16px';
      case 'inactive':
      case 'approved':
      case 'rejected':
      case 'paid':
      case 'completed':
        return '12px';
      case 'closed':
      case 'suspended':
        return '14px';
      case 'pending':
      case 'pending approval':
      case 'payment progress':
      case 'payment pending':
        return '10px';
      case 'verified':
      case 'unverified':
        return '14px';
      default:
        return '14px';
    }
  };

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: getColor(),
    marginRight: '4px',
    fontSize: getFontSize(),
  };
});

interface StyledStatusTextProps {
  statusType:
    | 'active'
    | 'inactive'
    | 'closed'
    | 'suspended'
    | 'new'
    | 'pending'
    | 'pending approval'
    | 'approved'
    | 'rejected'
    | 'payment progress'
    | 'payment pending'
    | 'paid'
    | 'completed'
    | 'payment completed'
    | 'issued'
    | 'realized'
    | 'returned'
    | 'cancelled'
    | 'verified'
    | 'unverified';
}

export const StyledStatusText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'statusType',
})<StyledStatusTextProps>(({ statusType }) => {
  const getColor = () => {
    switch (statusType) {
      case 'active':
      case 'approved':
      case 'verified':
        return 'var(--success)';
      case 'paid':
      case 'completed':
        return 'var(--completed, #00897B)';
      case 'payment completed':
        return 'var(--payment-completed, #7B1FA2)';
      case 'inactive':
      case 'rejected':
      case 'unverified':
        return 'var(--error)';
      case 'pending':
      case 'pending approval':
        return 'var(--warning)';
      case 'payment pending':
        return 'var(--payment-pending, #EF6C00)';
      case 'new':
        return 'var(--new-status)';
      case 'closed':
      case 'suspended':
        return 'var(--instructions)';
      case 'payment progress':
        return 'var(--info, #1976D2)';
      case 'issued':
        return 'var(--issued, #1976D2)';
      case 'realized':
        return 'var(--realized, #388E3C)';
      case 'returned':
        return 'var(--returned, #F57C00)';
      case 'cancelled':
        return 'var(--cancelled, #D32F2F)';
      default:
        return 'var(--primary-navy)';
    }
  };

  return {
    fontFamily: 'Outfit',
    fontWeight: 400,
    fontSize: '12px',
    color: getColor(),
  };
});

export const getStatusIconClass = (status: string): string => {
  if (!status) return 'bx bx-minus-circle';
  const normalizedStatus = status.toLowerCase();

  switch (normalizedStatus) {
    case 'active':
    case 'new':
      return 'bx bx-radio-circle-marked';
    case 'inactive':
      return 'bx bx-minus-circle';
    case 'closed':
    case 'suspended':
      return 'bx bx-x-circle';
    case 'approved':
    case 'paid':
    case 'completed':
    case 'payment completed':
      return 'bx bxs-check-circle';
    case 'rejected':
      return 'bx bxs-x-circle';
    case 'pending':
    case 'pending approval':
    case 'payment pending':
      return 'bx bxs-circle';
    case 'payment progress':
      return 'bx bx-loader-circle';
    case 'issued':
      return 'bx bxs-circle';
    case 'realized':
      return 'bx bxs-check-circle';
    case 'returned':
      return 'bx bx-revision';
    case 'cancelled':
      return 'bx bxs-x-circle';
    case 'verified':
      return 'bx bxs-check-shield';
    case 'unverified':
      return 'bx bx-shield-x';
    default:
      return 'bx bx-radio-circle-marked';
  }
};
